import { useState } from 'react';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Space,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router';
import { authApi } from '@/core/api/authApi';
import { useAuthStore } from '@/core/auth/authStore';
import { getErrorMessage } from '@/core/utils/errors';

const loginSchema = z.object({
  tenantCode: z.string().trim().min(2, 'کد سازمان الزامی است.'),
  username: z.string().trim().min(3, 'نام کاربری را وارد کنید.'),
  password: z.string().min(6, 'رمز عبور حداقل ۶ کاراکتر است.'),
});

type LoginValues = z.infer<typeof loginSchema>;

export const LoginPage = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = AntdApp.useApp();
  const setTokens = useAuthStore((state) => state.setTokens);
  const setBootstrapData = useAuthStore((state) => state.setBootstrapData);
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      tenantCode: searchParams.get('tenant')?.trim() || '',
      username: '',
      password: '',
    },
  });

  const submit = async (values: LoginValues): Promise<void> => {
    setServerError(null);
    try {
      const tokens = await authApi.login(values);
      setTokens(tokens);
      const bootstrap = await authApi.bootstrap();
      setBootstrapData(bootstrap);
      message.success(`خوش آمدید، ${bootstrap.me.membership.displayName}`);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setServerError(getErrorMessage(error));
    }
  };

  return (
    <div className="login-page">
      <section className="login-visual-panel">
        <div className="login-visual-panel__content">
          <div className="login-logo">C</div>
          <Typography.Title className="login-hero-title">
            ارتباط با مشتری،
            <br />
            شفاف و قابل پیگیری
          </Typography.Title>
          <Typography.Paragraph className="login-hero-description">
            داشبورد یکپارچه مدیران و کارشناسان برای ثبت، اولویت‌بندی، تخصیص و پیگیری سرنخ‌های فروش.
          </Typography.Paragraph>

          <Space orientation="vertical" size={18} className="login-benefits">
            <Flex align="center" gap={12}>
              <CheckCircleFilled />
              <span>نمای مدیریتی از وضعیت تمام سرنخ‌ها</span>
            </Flex>
            <Flex align="center" gap={12}>
              <CheckCircleFilled />
              <span>میزکار شخصی برای هر کارشناس فروش</span>
            </Flex>
            <Flex align="center" gap={12}>
              <CheckCircleFilled />
              <span>دسترسی مبتنی بر Permission و Data Scope</span>
            </Flex>
          </Space>
        </div>
        <div className="login-visual-panel__orb login-visual-panel__orb--one" />
        <div className="login-visual-panel__orb login-visual-panel__orb--two" />
      </section>

      <section className="login-form-panel">
        <Card className="login-card" variant="borderless">
          <div className="login-card__header">
            <Typography.Title level={2}>ورود به سامانه</Typography.Title>
            <Typography.Paragraph type="secondary">
              کد سازمانی، نام کاربری و رمز عبور تعریف‌شده توسط مدیر پلتفرم را وارد کنید.
            </Typography.Paragraph>
          </div>

          {serverError ? (
            <Alert
              showIcon
              type="error"
              title="ورود ناموفق بود"
              description={serverError}
              closable
              onClose={() => setServerError(null)}
              className="login-alert"
            />
          ) : null}

          <Form layout="vertical" onFinish={() => void handleSubmit(submit)()} requiredMark={false}>
            <Controller
              name="tenantCode"
              control={control}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="کد سازمان"
                  validateStatus={fieldState.error ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    size="large"
                    prefix={<SafetyCertificateOutlined />}
                    placeholder="کد سازمانی"
                    autoComplete="organization"
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="username"
              control={control}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="نام کاربری"
                  validateStatus={fieldState.error ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    size="large"
                    prefix={<UserOutlined />}
                    placeholder="نام کاربری تعریف‌شده"
                    autoComplete="username"
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="رمز عبور"
                  validateStatus={fieldState.error ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <Input.Password
                    {...field}
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="رمز عبور"
                    autoComplete="current-password"
                  />
                </Form.Item>
              )}
            />
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isSubmitting}
              icon={<ArrowLeftOutlined />}
              iconPlacement="end"
            >
              ورود به داشبورد
            </Button>
          </Form>
        </Card>
      </section>
    </div>
  );
};
